import { r, w, t } from '@arrow-js/core'

const defaultOptions = {
	activeClass: 'current-menu-item',
	childSelector: 'li',
	focusedChildClass: 'is--focused-child',
	focusedIndicator: true,
	hasActiveIndicatorClass: 'has--active-indicator',
	focusedIndicatorClass: 'indicator',
	selectedEventName: 'selected-item-updated',
	selectedIndicatorClass: 'indicator indicator--selected',
	selectedIndicator: true,
}

export class SharedIndicator {
	constructor(el) {
		this.beforeSetup();
		this.el = el;
		this.slug = el.dataset.sharedIndicator;
		this.children = [];
		this.focusedIndicator = null;
		this.selectedIndicator = null;
		this.initialLoad = false;

		if (el.dataset.sharedIndicatorOptions) {
			this.options = Object.assign({}, defaultOptions, JSON.parse(el.dataset.sharedIndicatorOptions));
		} else {
			this.options = Object.assign({}, defaultOptions);
		}

		this.classes = {
			activeItem: this.options.activeClass,
			focusedChild: this.options.focusedChildClass,
			hasActiveIndicator: this.options.hasActiveIndicatorClass,
			focusedIndicator: this.options.focusedIndicatorClass,
			selectedIndicator: this.options.selectedIndicatorClass,
		}
		this.afterSetup();

		this.state = r({
			indicatorActive: false,
			focusedChild: null,
			selectedChild: null,
		})

		// computed properties
		this.childrenData = r({})

		// observers
		this.resizeObserver = null;

		// templates
		this.focusedIndicatorTemplate = t`
			<div class="${this.classes.focusedIndicator}" style="${() => this.updateIndicator(this.state.focusedChild)}" role="presentation"></div>
		`;

		this.selectedIndicatorTemplate = t`
			<div class="${this.classes.selectedIndicator}" style="${() => this.updateIndicator(this.state.selectedChild)}" role="presentation"></div>
		`;

		this.template = t`
			${() => this.options.focusedIndicator && this.focusedIndicatorTemplate }
			${() => this.options.selectedIndicator && this.selectedIndicatorTemplate }
		`;

		// init
		this.beforeInit();
		this.init();
		this.afterInit();
	}

	buildChildObject(child) {
		return {
			el: child,
			position: {
				x: null,
				y: null
			},
			size: {
				block: null,
				inline: null
			}
		}
	}

	findChildren(el) {
		this.children = [...this.el.querySelectorAll(this.options.childSelector)]
		if (!this.children.length){
			console.warn(`[SharedIndicator: ${this.slug}] No children found`);
			return;
		}
	}

	childrenInit() {
		this.children.forEach((child, index) => {
			// add index to child
			child.dataset.sharedIndicatorIndex = `${this.slug}-child-${index}`

			// add to childrenData
			this.childrenData[child.dataset.sharedIndicatorIndex] = this.buildChildObject(child)

			// add to resize observer
			this.resizeObserver.observe(child)

			// add event listeners
			child.addEventListener('mouseover', () => this.onChildFocusIn(child))
			child.addEventListener('focusin', () => this.onChildFocusIn(child))
		});
	}

	onChildFocusIn(child) {
		const childIndex = child.dataset.sharedIndicatorIndex;
		this.state.focusedChild = this.childrenData[childIndex];
	}

	updateIndicator(childData) {
		if (!childData) { return null }
		const styles = [
			`--indicator-inset-block-start: ${childData.position.y}px;`,
			`--indicator-inset-inline-start: ${childData.position.x}px;`,
			`--indicator-inline-size: ${childData.size.inline}px;`,
			`--indicator-block-size: ${childData.size.block}px;`,
		]
		return styles.join(' ')
	}

	buildResizeObserver() {
		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const _child = entry.target.dataset.sharedIndicatorIndex;
				this.childrenData[_child].position.x = entry.target.offsetLeft;
				this.childrenData[_child].position.y = entry.target.offsetTop;
				this.childrenData[_child].size.inline = entry.borderBoxSize[0].inlineSize;
				this.childrenData[_child].size.block = entry.borderBoxSize[0].blockSize;
			}
		});
	}

	active() {
		this.state.indicatorActive = true;
		this.el.classList.add(this.classes.hasActiveIndicator);
	}

	inactive() {
		this.state.indicatorActive = false;
		this.el.classList.remove(this.classes.hasActiveIndicator);
	}

	addEventListeners() {
		this.el?.addEventListener('mouseenter', () => this.active());
		this.el?.addEventListener('mouseleave', () => this.inactive());
		this.el?.addEventListener('focusin', () => this.active());
		this.el?.addEventListener('focusout', () => this.inactive());
	}

	integrateScrollspy() {
		if (!this.el.dataset.scrollspy) { return }
		this.scrollspyObserver = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'data-scrollspy-selected') {
					const { target } = mutation
					if (target.dataset.scrollspySelected === 'true') {
						const targetParent = target.parentElement
						if (targetParent.dataset.sharedIndicatorIndex) {
							const activeChild = this.childrenData[targetParent.dataset.sharedIndicatorIndex]
							this.state.selectedChild = activeChild
							this.children.forEach(child => child.dataset.scrollspySelectedChild = child === activeChild.el)
							if (this.el.matches(':hover') && !this.state.indicatorActive) {
								setTimeout(() => {
									this.active()
								}, 500)
							}
						}
					}
				}
			})
		})

		const config = { attributes: true, childList: true, subtree: true }
		this.scrollspyObserver.observe(this.el, config)
	}

	buildIndicator() {
		this.template(this.el);
	}

	initSelectedIndicator() {
		if (!this.options.selectedIndicator) { return }
		if (this.state.selectedChild) { return }
		this.state.selectedChild = this.childrenData[`${this.slug}-child-0`]
		this.state.selectedChild.el.dataset.scrollspySelectedChild = 'true'
		const childLink = this.state.selectedChild.el.querySelector('a')
		if (childLink) { childLink.dataset.scrollspySelected = 'true' }
	}

	init() {
		// find children
		this.findChildren(this.el);

		// add event listeners
		this.addEventListeners();

		// build indicator
		this.buildIndicator();

		// build observers
		this.buildResizeObserver();

		// init children
		this.childrenInit();

		// integrate scrollspy
		this.integrateScrollspy();

		// init selected indicator
		setTimeout(() => this.initSelectedIndicator(), 250)
	}

	beforeSetup() {}
	afterSetup() {}
	beforeInit() {}
	afterInit() {}
}
